import React, { useState, useEffect, useMemo } from 'react';
import {
  Container,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TextField,
  FormControl,
  TablePagination,
} from '@mui/material';
import { withAuthenticationRequired } from '@auth0/auth0-react';
import { getAllOrganizations } from '@/repository/OrganizationDAO';
import { useHistory } from 'react-router-dom';
import CreateOrganizationModal from './modals/CreateOrganizationModal';
import AddIcon from '@mui/icons-material/Add';
import { useUserContext } from '@/context_providers/user_context/UserProvider';
import { useSafeguardAdminSignalRContext } from '@/context_providers/signal_r_context/SafeguardAdminSignalRListener';
import Loading from '@/components/loading/Loading';
import COLORS from '@/resources/style/Themes';
import PageHeader from '@/components/utils/PageHeader';
import { useLoading } from '@/context_providers/loading_context/LoadingContext';
import {
  GetAllOrgResponse,
  OrganizationOnAzure,
  SubscriptionTier,
} from '@/../generated-client/models';
import { buildPoolCounts } from '@/utils/organization/orgSeatAvailability';
import { getTierLabelKey } from '@/utils/entitlements/tierLabels';

type OrgKeys = keyof GetAllOrgResponse;

export const ManageOrganizations = () => {
  const [responseData, setResponseData] = useState<GetAllOrgResponse[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const history = useHistory();
  const [showCreateOrganizationModal, setShowCreateOrganizationModal] =
    useState(false);
  const { setOrgCode, connection } = useSafeguardAdminSignalRContext();
  const { isSafeguardAdmin, isSafeguardDev } = useUserContext();
  const [sortConfig, setSortConfig] = useState<{
    key: OrgKeys | null;
    direction: string;
  }>({
    key: null,
    direction: 'ascending',
  });
  const { setPageLoading } = useLoading();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const opsLabel = getTierLabelKey(SubscriptionTier.Ops);
  const respondLabel = getTierLabelKey(SubscriptionTier.Respond);

  useEffect(() => {
    const fetchAllOrganizationData = async () => {
      if (isSafeguardAdmin || isSafeguardDev) {
        try {
          const organizations = await getAllOrganizations();
          requestSort('company_name');
          setResponseData(organizations);
        } catch (e) {
          console.warn('Error fetching organizations');
        }
      }
    };
    fetchAllOrganizationData();
  }, [isSafeguardAdmin, isSafeguardDev]);

  useEffect(() => {
    if (responseData && connection) {
      setPageLoading(false);
    }
  }, [responseData, connection, setPageLoading]);

  const handleCompanyClick = (org_code: string) => {
    setOrgCode(org_code);
    history.push(`/organization`);
  };

  const requestSort = (key: OrgKeys) => {
    let direction = 'ascending';
    if (sortConfig.key === key && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    setSortConfig({ key, direction });
  };

  const handleCreateOrganization = (newOrg: OrganizationOnAzure) => {
    setResponseData((prevData) => {
      // A freshly created org has no groups yet, so seats-used sums to 0 via
      // buildPoolCounts — that's a genuine value here, not a fabricated
      // default (buildPoolCounts sums the per-group tier lists the same way
      // it does for every other org-summary consumer).
      const poolCounts = buildPoolCounts(newOrg);
      const responseConvert: GetAllOrgResponse = {
        company_name: newOrg.company_name,
        id: newOrg.id,
        is_safeguard_organization: newOrg.is_safeguard_organization,
        org_code: newOrg.org_code,
        subscription_count: newOrg.subscription_count,
        user_count: newOrg.subscription_count,
        ops_subscription_count: poolCounts.ops_subscription_count ?? 0,
        respond_subscription_count: poolCounts.respond_subscription_count ?? 0,
        ops_seats_used: poolCounts.ops_seats_used ?? 0,
        respond_seats_used: poolCounts.respond_seats_used ?? 0,
      };
      return [...prevData, responseConvert];
    });
  };

  const sortedData = useMemo(() => {
    let sortableItems = [...responseData];
    const sortKey = sortConfig.key;
    if (sortKey !== null) {
      sortableItems.sort((a, b) => {
        if ((a[sortKey] ?? 0) < (b[sortKey] ?? 0))
          return sortConfig.direction === 'ascending' ? -1 : 1;
        if ((a[sortKey] ?? 0) > (b[sortKey] ?? 0))
          return sortConfig.direction === 'ascending' ? 1 : -1;
        return 0;
      });
    }
    return sortableItems;
  }, [responseData, sortConfig]);

  const renderData = () => {
    const filteredData = sortedData.filter(
      (organization) =>
        organization.company_name
          .toLowerCase()
          .includes(searchTerm.toLowerCase()) ||
        organization.org_code.toLowerCase().includes(searchTerm.toLowerCase()),
    );

    const paginatedData =
      rowsPerPage > 0
        ? filteredData.slice(
            page * rowsPerPage,
            page * rowsPerPage + rowsPerPage,
          )
        : filteredData;

    return (
      <>
        <PageHeader
          disabled={false}
          action={() => setShowCreateOrganizationModal(true)}
          icon={<AddIcon style={{ color: COLORS.WHITE }} />}
          header={'Manage Organizations'}
          label={'Add New'}
        />
        <FormControl fullWidth sx={{ mb: 2 }}>
          <TextField
            placeholder="Search..."
            variant="outlined"
            onChange={(event) => setSearchTerm(event.target.value)}
            slotProps={{
              input: {
                style: {
                  height: '50px',
                  padding: '10px',
                },
              },
            }}
          />
        </FormControl>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell
                style={{
                  background: COLORS.SAFEGUARD_BLUE,
                  color: COLORS.WHITE,
                  cursor: 'pointer',
                }}
                onClick={() => requestSort('company_name')}
              >
                Organization
                {sortConfig.key === 'company_name'
                  ? sortConfig.direction === 'ascending'
                    ? ' ▲'
                    : ' ▼'
                  : sortConfig.key
                    ? ''
                    : ' ▲'}
              </TableCell>
              <TableCell
                style={{
                  background: COLORS.SAFEGUARD_BLUE,
                  color: COLORS.WHITE,
                  cursor: 'pointer',
                }}
                onClick={() => requestSort('org_code')}
              >
                Code
                {sortConfig.key === 'org_code'
                  ? sortConfig.direction === 'ascending'
                    ? ' ▲'
                    : ' ▼'
                  : sortConfig.key
                    ? ''
                    : ' ▲'}
              </TableCell>
              <TableCell
                style={{
                  background: COLORS.SAFEGUARD_BLUE,
                  color: COLORS.WHITE,
                  cursor: 'pointer',
                  textAlign: 'end',
                }}
                onClick={() => requestSort('ops_subscription_count')}
              >
                {opsLabel} Subscription Count
                {sortConfig.key === 'ops_subscription_count'
                  ? sortConfig.direction === 'ascending'
                    ? ' ▲'
                    : ' ▼'
                  : sortConfig.key
                    ? ''
                    : ' ▲'}
              </TableCell>
              <TableCell
                style={{
                  background: COLORS.SAFEGUARD_BLUE,
                  color: COLORS.WHITE,
                  cursor: 'pointer',
                  textAlign: 'end',
                }}
                onClick={() => requestSort('respond_subscription_count')}
              >
                {respondLabel} Subscription Count
                {sortConfig.key === 'respond_subscription_count'
                  ? sortConfig.direction === 'ascending'
                    ? ' ▲'
                    : ' ▼'
                  : sortConfig.key
                    ? ''
                    : ' ▲'}
              </TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {paginatedData.map((organization, index) => (
              <TableRow
                key={index}
                onClick={() => handleCompanyClick(organization.org_code)}
                style={{ cursor: 'pointer' }}
              >
                <TableCell>{organization.company_name}</TableCell>
                <TableCell>{organization.org_code}</TableCell>
                <TableCell sx={{ textAlign: 'end' }}>
                  {organization.ops_subscription_count}
                </TableCell>
                <TableCell sx={{ textAlign: 'end' }}>
                  {organization.respond_subscription_count}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[10, 25, 100, { label: 'All', value: -1 }]}
          component="div"
          count={filteredData.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(event, newPage) => setPage(newPage)}
          onRowsPerPageChange={(event) => {
            setRowsPerPage(parseInt(event.target.value, 10));
            setPage(0);
          }}
        />
      </>
    );
  };

  return (
    <Container maxWidth="xl">
      {responseData && connection ? renderData() : null}
      <CreateOrganizationModal
        show={showCreateOrganizationModal}
        onHide={() => setShowCreateOrganizationModal(false)}
        onCreate={handleCreateOrganization}
      />
    </Container>
  );
};

export default withAuthenticationRequired(ManageOrganizations, {
  onRedirecting: () => <Loading loadingText={'Loading...'} />,
});
